// FileStage — the half of the file surface that RENDERS a file, by family, with
// what the browser gives (FRONTEND-PATTERNS §Files): an image on ZoomStage
// (zoom · pan · swipe), the native video and audio players, the native PDF
// frame, and a plain card with Download for what has no preview (office · CAD ·
// archive). Two modes: `hero` — the picture at the top of the docked panel /
// the phone page, with the View door to full screen; `full` — the whole stage,
// arrows for the walk. The ground is the stage token (dark by design); the
// source is the caller's (a signed link, minted per open, null while minting) —
// the stage never loads one past its expiry: it waits and asks for a fresh one.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';
import { Spinner } from '../ui/Spinner.js';
import { ZoomStage } from '../ui/ZoomStage.js';
import { icons } from '../ui/icons.js';
import { formatBytes } from '../../lib/format.js';
import { FamilyGlyph, displayName, type LibraryFile } from './filesDisplay.js';

export interface FileStageProps {
  file: LibraryFile;
  /** The resolved source (the files origin's signed link); null while it mints. */
  src: string | null;
  /** When `src` goes dead for a NEW load (the client's clock, ms). Checked once,
   *  at the link's first render: past it the stage waits and asks `onSourceExpired`
   *  — a link already loaded is never disturbed when its TTL passes. */
  srcExpiresAt?: number | null;
  /** The mint refused, or the bytes refused after the host's one retry: the card says so. */
  failed?: boolean;
  mode: 'hero' | 'full';
  /** `full`: the walk's arrows + swipe; `hero`: a swipe on the picture only. */
  onSwipePrev?: () => void;
  onSwipeNext?: () => void;
  /** `hero`: the View door (expand to the full stage). */
  onExpand?: () => void;
  /** The no-preview card's one act. */
  onDownload?: (file: LibraryFile) => void;
  /** A signed link died before the element loaded — the caller re-mints once. */
  onSourceError?: () => void;
  /** The stage met a link past its expiry — the caller mints again (a fresh `src` follows). */
  onSourceExpired?: () => void;
  /** The dark stage ground (default: the full face, or a picture) — beside the
   *  rail a document's card and the PDF frame sit on the light surface. */
  stage?: boolean;
  /** The walk's ‹ › on the stage (default: the full face) — the full SCREEN
   *  only; beside the rail the footer's pager walks. */
  arrows?: boolean;
  className?: string;
}

export function FileStage({ file, src, srcExpiresAt = null, failed = false, mode, onSwipePrev, onSwipeNext, onExpand, onDownload, onSourceError, onSourceExpired, stage, arrows, className = '' }: FileStageProps) {
  const { t } = useTranslation('files');
  const name = displayName(file);
  const full = mode === 'full';
  // The ground: the dark STAGE for a picture and for every full-screen face; in
  // the hero a document or a voice note sits on the panel's own surface (a
  // black box on a light screen was the wrong weight for a card).
  const picture = file.family === 'image' || file.family === 'video';
  const onStage = stage ?? (full || picture);
  const showArrows = arrows ?? full;
  const muted = onStage ? 'text-[var(--color-on-stage-muted)]' : 'text-[var(--color-text-secondary)]';
  const ground = `relative flex items-center justify-center overflow-hidden ${onStage ? 'bg-[var(--color-bg-stage)] text-[var(--color-on-stage)]' : 'bg-[var(--color-bg-surface-alt)] text-[var(--color-text-primary)]'} ${full ? 'h-full w-full' : picture ? 'h-full min-h-[14rem] w-full' : 'w-full py-6'} ${className}`;

  const waiting = (label: string) => (
    <div role="status" className={`flex flex-col items-center gap-2 text-sm ${muted}`}>
      <Spinner />
      {label}
    </div>
  );

  let body: React.ReactNode;
  // A document's hero is its CARD (the typed glyph · the name · the facts line ·
  // the View door): the browser's PDF reader shows in the full stage only — an
  // embedded reader in a small box brings its toolbar and its own scroll.
  const pdfHero = file.family === 'pdf' && !full;
  if (failed || !file.previewable || pdfHero || file.family === 'document' || file.family === 'text' || file.family === 'cad' || file.family === 'archive') {
    body = (
      <div className="flex max-w-sm flex-col items-center gap-3 px-6 text-center">
        <span className={muted}>
          <FamilyGlyph family={file.family} contentType={file.content_type} size={48} />
        </span>
        <span className="text-base font-semibold">{name}</span>
        <span className={`text-sm ${muted}`}>
          {/* A PDF has its preview — in the full stage; the others have none; a refused one says so below. */}
          {[t(`family.${file.family}`), formatBytes(file.size_bytes), pdfHero || failed ? null : t('preview.noPreview')].filter(Boolean).join(' · ')}
        </span>
        {failed && (
          <span role="status" className="text-sm">
            {t('preview.failed')}
          </span>
        )}
        {pdfHero && !failed && onExpand ? (
          <Button variant="secondary" onClick={onExpand}>
            <icons.expand size={16} aria-hidden />
            {t('stage.view')}
          </Button>
        ) : (
          onDownload && (
            <Button variant={onStage ? 'stage' : 'secondary'} onClick={() => onDownload(file)}>
              <icons.download size={16} aria-hidden />
              {t('actions.download')}
            </Button>
          )
        )}
      </div>
    );
  } else if (src === null) {
    body = waiting(t('preview.loading'));
  } else {
    let element: React.ReactNode;
    if (file.family === 'image') {
      element = full ? (
        <ZoomStage
          src={src}
          alt={name}
          onSwipePrev={onSwipePrev}
          onSwipeNext={onSwipeNext}
          onError={onSourceError}
          zoomInLabel={t('stage.zoomIn')}
          zoomOutLabel={t('stage.zoomOut')}
          resetLabel={t('stage.resetZoom')}
          instructions={t('stage.instructions')}
        />
      ) : (
        // The hero: a click opens the full stage (the View door below says the same).
        <button type="button" aria-label={t('stage.view')} onClick={onExpand} className="h-full w-full outline-none focus-ring-inset">
          <img src={src} alt="" onError={onSourceError} className="h-full w-full object-contain" />
        </button>
      );
    } else if (file.family === 'video') {
      element = (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- a field capture carries no track
        <video src={src} controls poster={file.thumb ?? undefined} onError={onSourceError} className="max-h-full max-w-full" />
      );
    } else if (file.family === 'audio') {
      element = (
        <div className={`flex w-[min(92%,30rem)] items-center gap-3 rounded-lg p-4 ${onStage ? 'bg-[var(--color-on-stage)]/10' : 'bg-[var(--color-bg-surface)]'}`}>
          <span className={muted}>
            <icons.audio size={28} aria-hidden />
          </span>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a voice note carries no track */}
          <audio src={src} controls onError={onSourceError} className="w-full" />
        </div>
      );
    } else {
      element = <PdfFrame src={src} title={name} onStage={onStage} waiting={waiting(t('preview.downloading', { size: formatBytes(file.size_bytes) }))} />;
    }
    // One gate per link (`key`): a link past its expiry at first render is never requested.
    body = (
      <SourceGate key={src} expiresAt={srcExpiresAt} onExpired={onSourceExpired} waiting={waiting(t('preview.loading'))}>
        {element}
      </SourceGate>
    );
  }

  return (
    <div className={ground}>
      {body}
      {showArrows && onSwipePrev && (
        <IconButton aria-label={t('stage.previous')} variant="stage" onClick={onSwipePrev} className="absolute start-3 top-1/2 -translate-y-1/2 rounded-full">
          <icons.caretLeft size={22} aria-hidden className="rtl:rotate-180" />
        </IconButton>
      )}
      {showArrows && onSwipeNext && (
        <IconButton aria-label={t('stage.next')} variant="stage" onClick={onSwipeNext} className="absolute end-3 top-1/2 -translate-y-1/2 rounded-full">
          <icons.caretRight size={22} aria-hidden className="rtl:rotate-180" />
        </IconButton>
      )}
      {!full && onExpand && (file.family === 'image' || file.family === 'video') && (
        <span className="absolute bottom-3 end-3">
          <Button variant="stage" onClick={onExpand}>
            <icons.expand size={16} aria-hidden />
            {t('stage.view')}
          </Button>
        </span>
      )}
    </div>
  );
}

/** One instance per link (the caller's `key`): a link past its client-side expiry
 *  at its first render is never requested — a frame reports no error for a dead
 *  link, so the person would see a blank — the stage waits and asks the host once;
 *  a link that passed the gate is left alone for its life (a PDF being read, a
 *  video playing) however long the stage stays open. */
function SourceGate({ expiresAt, onExpired, waiting, children }: { expiresAt: number | null; onExpired?: () => void; waiting: React.ReactNode; children: React.ReactNode }) {
  const [expired] = useState(() => expiresAt !== null && onExpired !== undefined && Date.now() >= expiresAt);
  const latest = useRef(onExpired);
  // Asked once for the life of this link — the latch holds through StrictMode's
  // double-run of the effect (a second ask would be a second mint).
  const asked = useRef(false);
  useEffect(() => {
    latest.current = onExpired;
  });
  useEffect(() => {
    if (!expired || asked.current) return;
    asked.current = true;
    latest.current?.();
  }, [expired]);
  return expired ? waiting : children;
}

/** The browser's own PDF reader, behind the stage's status until the frame's
 *  `load` event. Safari paints a PDF only once it has all of it and shows no
 *  progress of its own — a white frame for as long as the bytes take; Chrome and
 *  Firefox fire `load` as soon as their reader shell is up and carry their own
 *  progress bar from there. The frame stays in the tree under the status (a
 *  hidden frame defers the reader). The fragment carries the open parameters
 *  Chrome and Firefox honour and Safari ignores; it never reaches the server. */
function PdfFrame({ src, title, onStage, waiting }: { src: string; title: string; onStage: boolean; waiting: React.ReactNode }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative h-full w-full">
      <iframe src={`${src}#zoom=page-width&navpanes=0`} title={title} onLoad={() => setLoaded(true)} className={`h-full w-full border-0 ${onStage ? '' : 'bg-[var(--color-bg-surface)]'}`} />
      {!loaded && <div className={`absolute inset-0 flex items-center justify-center ${onStage ? 'bg-[var(--color-bg-stage)]' : 'bg-[var(--color-bg-surface-alt)]'}`}>{waiting}</div>}
    </div>
  );
}
